import React, { useState } from 'react';
import { redirect, useLoaderData, useNavigation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import customFetch from '../../../../utils/customFetch';
import CardVorschau from '../../../bestellungen/CardVorschau';
import { CARD_STATUS } from '../../../../../../utils/constants';
import DeleteCardModal from './DeleteCardModal';

export const loader = async ({ params }) => {
  try {
    const { data } = await customFetch.get(`/cards/${params.id}`);
    return { card: data.singleCard };
  } catch (error) {
    toast.error(error?.response?.data?.msg);
    return redirect('/dashboard/kartenadministration');
  }
};

const CardDetail = () => {
  const { card } = useLoaderData();
  const [cardUrl, setCardUrl] = useState(card.url);
  const [cardStatus, setCardStatus] = useState(card.status);
  const [cardOwner, setCardOwner] = useState(card.owner._id);

  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  const updateCard = async (event) => {
    event.preventDefault();
    const data = {
      status: cardStatus,
      url: cardUrl,
      owner: cardOwner,
    };

    try {
      await customFetch.patch(`/cards/${card._id}`, data);
      toast.success('Karte erfolgreich aktualisiert!');
      return;
    } catch (error) {
      toast.error(error?.response?.data?.msg);
      return error;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success('Kopiert!');
      })
      .catch((err) => {
        toast.error('Failed to copy: ', err);
      });
  };

  // Parse graphic data if it's JSON
  const parseGraphicData = (graphicData) => {
    if (graphicData && graphicData.startsWith('{"Farbe')) {
      try {
        return JSON.parse(graphicData);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const frontGraphicData = parseGraphicData(card.graphicFront);
  const backGraphicData = parseGraphicData(card.graphicBack);

  // Card Delete Modal
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  return (
    <>
      <nav aria-label="Breadcrumb">
        <ol role="list" className="flex items-center space-x-2">
          <li>
            <div className="flex items-center text-sm">
              <a
                href="/dashboard/kartenadministration"
                className="font-medium text-gray-500 hover:text-gray-900"
              >
                Zurück zu allen Karten
              </a>
            </div>
          </li>
        </ol>
      </nav>

      <DeleteCardModal
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        cardId={card._id}
      />

      <main className="mx-auto max-w-2xl pb-24 pt-8 sm:px-6 sm:pt-16 lg:max-w-7xl lg:px-8">
        <div className="space-y-2 px-4 sm:flex sm:items-baseline sm:justify-between sm:space-y-0 sm:px-0">
          <div className="flex sm:items-baseline sm:space-x-4">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              {card.name}
            </h1>
          </div>
          <p className="text-sm text-gray-600">
            Erstellt am{' '}
            <time
              dateTime={card.createdAt.split('T')[0]}
              className="font-medium text-gray-900"
            >
              {new Date(card.createdAt).toLocaleDateString('de-CH')}
            </time>
          </p>
        </div>

        {/* Card Preview */}
        <section aria-labelledby="card-preview-heading" className="mt-6">
          <h2 id="card-preview-heading" className="sr-only">
            Kartenvorschau
          </h2>

          <div className="border-b border-t border-gray-200 bg-white shadow-xs sm:rounded-lg sm:border">
            <div className="px-4 py-6 sm:px-6 lg:grid lg:grid-cols-12 lg:gap-x-8 lg:p-8">
              <div className="sm:flex lg:col-span-7 gap-x-4">
                <div className="w-full">
                  <h3 className="text-base font-medium text-gray-900 mb-2">
                    Vorderseite
                  </h3>
                  <CardVorschau
                    className="h-full w-full object-cover object-center sm:h-full sm:w-full"
                    bildInfos={card.graphicFront}
                  />
                </div>

                <div className="w-full mt-4 sm:mt-0">
                  <h3 className="text-base font-medium text-gray-900 mb-2">
                    Rückseite
                  </h3>
                  <CardVorschau
                    className="h-full w-full object-cover object-center sm:h-full sm:w-full"
                    bildInfos={card.graphicBack}
                  />
                </div>
              </div>

              <div className="mt-6 lg:col-span-5 lg:mt-0">
                <dl className="space-y-4">
                  <div>
                    <dt className="text-base font-medium text-gray-900">
                      Karten-ID
                    </dt>
                    <dd className="mt-1 text-sm text-gray-500">{card._id}</dd>
                  </div>

                  <div>
                    <dt className="text-base font-medium text-gray-900">
                      Status
                    </dt>
                    <dd className="mt-1">
                      <span
                        className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                          card.status === 'active'
                            ? 'bg-green-50 text-green-700 ring-green-600/20'
                            : 'bg-gray-50 text-gray-600 ring-gray-500/10'
                        }`}
                      >
                        {card.status}
                      </span>
                    </dd>
                  </div>

                  <div>
                    <dt className="text-base font-medium text-gray-900">
                      Bestellnummer
                    </dt>
                    <dd className="mt-1 text-sm text-gray-500">
                      {card.orderId ? (
                        <a
                          href={`/dashboard/bestellbearbeitung/${card.orderId}`}
                          className="text-svisit-primary-600 hover:text-svisit-primary-500"
                        >
                          #{card.orderId}
                        </a>
                      ) : (
                        'Keine Bestellung verknüpft'
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Design Details */}
            <div className="border-t border-gray-200 px-4 py-6 sm:px-6 lg:p-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Design Details
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Vorderseite */}
                <div>
                  <h4 className="text-base font-medium text-gray-900">
                    Vorderseite
                  </h4>
                  {frontGraphicData ? (
                    <div className="flex-col space-y-2 mt-2">
                      <p
                        className="max-w-fit rounded-md border-0 px-2.5 py-2 shadow-xs ring-1 cursor-pointer bg-gray-50 text-gray-500 ring-gray-200 sm:text-sm sm:leading-6"
                        onClick={() => copyToClipboard(frontGraphicData.Farbe)}
                      >
                        Farbe: {frontGraphicData.Farbe}
                      </p>
                      {frontGraphicData.QR_Code && (
                        <p
                          className="max-w-fit rounded-md border-0 px-2.5 py-2 shadow-xs ring-1 cursor-pointer bg-gray-50 text-gray-500 ring-gray-200 sm:text-sm sm:leading-6"
                          onClick={() =>
                            copyToClipboard(frontGraphicData.QR_Code_Farbe)
                          }
                        >
                          QR Code Farbe: {frontGraphicData.QR_Code_Farbe}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p
                      className="overflow-hidden rounded-md border-0 mt-2 px-2.5 py-2 shadow-xs ring-1 cursor-pointer bg-gray-50 text-gray-500 ring-gray-200 sm:text-sm sm:leading-6"
                      onClick={() => copyToClipboard(card.graphicFront)}
                    >
                      {card.graphicFront}
                    </p>
                  )}
                </div>

                {/* Rückseite */}
                <div>
                  <h4 className="text-base font-medium text-gray-900">
                    Rückseite
                  </h4>
                  {backGraphicData ? (
                    <div className="flex-col space-y-2 mt-2">
                      <p
                        className="max-w-fit rounded-md border-0 px-2.5 py-2 shadow-xs ring-1 cursor-pointer bg-gray-50 text-gray-500 ring-gray-200 sm:text-sm sm:leading-6"
                        onClick={() => copyToClipboard(backGraphicData.Farbe)}
                      >
                        Farbe: {backGraphicData.Farbe}
                      </p>
                      {backGraphicData.QR_Code && (
                        <p
                          className="max-w-fit rounded-md border-0 px-2.5 py-2 shadow-xs ring-1 cursor-pointer bg-gray-50 text-gray-500 ring-gray-200 sm:text-sm sm:leading-6"
                          onClick={() =>
                            copyToClipboard(backGraphicData.QR_Code_Farbe)
                          }
                        >
                          QR Code Farbe: {backGraphicData.QR_Code_Farbe}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p
                      className="overflow-hidden rounded-md border-0 mt-2 px-2.5 py-2 shadow-xs ring-1 cursor-pointer bg-gray-50 text-gray-500 ring-gray-200 sm:text-sm sm:leading-6"
                      onClick={() => copyToClipboard(card.graphicBack)}
                    >
                      {card.graphicBack}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Owner Information & Card Management */}
        <section aria-labelledby="management-heading" className="mt-8">
          <h2 id="management-heading" className="sr-only">
            Kartenverwaltung und Besitzerinformationen
          </h2>

          <div className="bg-gray-100 px-4 py-6 sm:rounded-lg sm:px-6 lg:grid lg:grid-cols-12 lg:gap-x-8 lg:px-8 lg:py-8">
            {/* Owner Information */}
            <div className="lg:col-span-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Besitzerinformationen
              </h3>
              {card.owner ? (
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="font-medium text-gray-900">Name</dt>
                    <dd className="mt-1 text-gray-500">
                      <Link
                        to={`/dashboard/benutzerverwaltung/${card.owner._id}`}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        {card.owner.firstName} {card.owner.lastName}
                      </Link>
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-900">E-Mail</dt>
                    <dd className="mt-1 text-gray-500">{card.owner.email}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-900">Benutzer-ID</dt>
                    <dd className="mt-1 text-gray-500">{card.owner._id}</dd>
                  </div>
                </dl>
              ) : (
                <p className="text-sm text-gray-500">
                  Kein Besitzer zugewiesen
                </p>
              )}
            </div>

            {/* Card Management Form */}
            <div className="mt-8 lg:mt-0 lg:col-span-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Kartenverwaltung
              </h3>

              <form onSubmit={updateCard} className="space-y-4">
                {/* URL Field */}
                <div>
                  <label
                    htmlFor="url"
                    className="block text-sm font-medium text-gray-900"
                  >
                    Karten-URL
                  </label>
                  <div className="mt-1 flex rounded-md shadow-xs">
                    <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
                      app.svisit.ch/l/
                    </span>
                    <input
                      type="text"
                      name="url"
                      id="url"
                      value={cardUrl}
                      onChange={(e) => setCardUrl(e.target.value)}
                      className="block w-full flex-1 rounded-none rounded-r-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Vollständige URL:
                    <span
                      className="text-svisit-primary-600 cursor-pointer ml-1"
                      onClick={() =>
                        copyToClipboard(`app.svisit.ch/l/${cardUrl}`)
                      }
                    >
                      app.svisit.ch/l/{cardUrl}
                    </span>
                  </p>
                </div>

                {/* Status Field */}
                <div>
                  <label
                    htmlFor="status"
                    className="block text-sm font-medium text-gray-900"
                  >
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={cardStatus}
                    onChange={(e) => setCardStatus(e.target.value)}
                    className="mt-1 block w-full rounded-md border-0 bg-white py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                  >
                    {Object.values(CARD_STATUS).map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Owner Field */}
                <div>
                  <label
                    htmlFor="owner"
                    className="block text-sm font-medium text-gray-900"
                  >
                    Besitzer ändern
                  </label>
                  <input
                    type="text"
                    name="owner"
                    id="owner"
                    value={cardOwner}
                    onChange={(e) => setCardOwner(e.target.value)}
                    className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    placeholder="Benutzer-ID eingeben"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Aktuelle Benutzer-ID: {card.owner._id}
                  </p>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400  focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500 hover:cursor-pointer"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Speichert...' : 'Änderungen speichern'}
                  </button>
                </div>
              </form>

              {/* Karte löschen */}
              <div className="pt-4">
                <p className="block text-sm font-medium text-gray-900">
                  Danger Area: Karte löschen
                </p>
                <button
                  type="button"
                  onClick={() => setOpenDeleteModal(true)}
                  className="mt-1 w-full rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:cursor-pointer"
                >
                  Karte löschen
                </button>
              </div>

              {/* Timestamps */}
              <div className="mt-6 space-y-2 text-xs text-gray-500">
                <p>
                  Erstellt: {new Date(card.createdAt).toLocaleString('de-CH')}
                </p>
                <p>
                  Zuletzt aktualisiert:{' '}
                  {new Date(card.updatedAt).toLocaleString('de-CH')}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default CardDetail;
